/**
 * Payment Service
 * Handles Razorpay payment integration
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || 
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');

/**
 * Initialize Razorpay Checkout
 * @param {Object} options - Razorpay checkout options
 * @returns {Promise} - Promise that resolves when payment is successful
 */
export const initializeRazorpayCheckout = (options) => {
  return new Promise((resolve, reject) => {
    // Ensure Razorpay is loaded
    if (typeof window.Razorpay === 'undefined') {
      reject(new Error('Razorpay script not loaded. Please refresh the page.'));
      return;
    }

    // Add handler for payment success
    const checkoutOptions = {
      ...options,
      handler: function (response) {
        resolve(response);
      },
      modal: {
        ondismiss: function () {
          reject(new Error('Payment cancelled by user'));
        },
      },
    };

    const rzp = new window.Razorpay(checkoutOptions);
    rzp.open();
  });
};

/**
 * Create order and open Razorpay checkout
 * @param {string} tier - 'starter' or 'pro'
 * @param {Object} userInfo - Optional user information for prefill
 * @returns {Promise} - Promise that resolves with payment response
 */
export const processPayment = async (tier = 'starter', userInfo = {}) => {
  try {
    // Get order from backend (backend creates order using Razorpay SDK)
    const response = await fetch(`${API_URL}/api/payments/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tier }),
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to create order');
    }

    const { order_id, key_id, amount, currency, description } = data.data;

    // Prepare checkout options (matching MCP server get_checkout_config format)
    const checkoutOptions = {
      key: key_id,
      amount: amount,
      currency: currency,
      order_id: order_id, // Use order_id from backend
      name: 'Pluckr',
      description: description,
      theme: {
        color: '#8BE98D', // Match Pluckr green
      },
      prefill: {
        email: userInfo.email || '',
        contact: userInfo.contact || '',
        name: userInfo.name || '',
      },
    };

    // Open Razorpay checkout
    const paymentResponse = await initializeRazorpayCheckout(checkoutOptions);

    // Verify payment with backend (include tier for plan update)
    const verifyResponse = await verifyPayment({
      razorpay_order_id: paymentResponse.razorpay_order_id,
      razorpay_payment_id: paymentResponse.razorpay_payment_id,
      razorpay_signature: paymentResponse.razorpay_signature,
      order_id: order_id,
      tier: tier,
    });

    // Update localStorage with new user data if returned
    if (verifyResponse.data?.user) {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...currentUser,
        plan: verifyResponse.data.user.plan,
        planPurchasedAt: verifyResponse.data.user.planPurchasedAt,
        unlockedMcps: verifyResponse.data.user.unlockedMcps,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }

    return {
      success: true,
      payment: paymentResponse,
      verification: verifyResponse,
    };
  } catch (error) {
    console.error('Payment error:', error);
    throw error;
  }
};

/**
 * Verify payment with backend
 * @param {Object} paymentData - Payment response from Razorpay
 * @returns {Promise} - Promise that resolves with verification result
 */
export const verifyPayment = async (paymentData) => {
  try {
    // Get auth token if available
    const authToken = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add auth header if user is logged in
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_URL}/api/payments/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify(paymentData),
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Payment verification failed');
    }

    return data;
  } catch (error) {
    console.error('Verification error:', error);
    throw error;
  }
};

/**
 * Get Razorpay key from backend
 * @returns {Promise} - Promise that resolves with key_id
 */
export const getRazorpayKey = async () => {
  try {
    const response = await fetch(`${API_URL}/api/payments/key`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to get Razorpay key');
    }

    return data.data.key_id;
  } catch (error) {
    console.error('Error getting Razorpay key:', error);
    throw error;
  }
};
